import axios from 'axios';

// In production (Netlify): use Netlify Function to bypass CORS
// In development: use a CORS proxy
const isProduction = process.env.NODE_ENV === 'production';
const SHOPIFY_STORE = 'luxurystyle.info';

// Cache for products (in-memory, resets on page reload)
let productsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Strip HTML tags and clean up text content
 */
const stripHtml = (html) => {
  if (!html) return '';
  // Create a temporary div to parse HTML properly
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  // Get text content and clean up whitespace
  let text = tmp.textContent || tmp.innerText || '';
  // Replace multiple whitespace with single space
  text = text.replace(/\s+/g, ' ').trim();
  return text;
};

/**
 * Transform Shopify product to frontend-friendly format
 */
const transformShopifyProduct = (product) => {
  const images = (product.images || []).map(img => ({
    id: String(img.id),
    url: img.src,
    altText: product.title,
    width: img.width,
    height: img.height
  }));

  const variants = (product.variants || []).map(variant => {
    const options = [];
    if (variant.option1 && variant.option1 !== "Default Title") {
      options.push({ name: "Size", value: variant.option1 });
    }
    if (variant.option2) {
      options.push({ name: "Color", value: variant.option2 });
    }
    if (variant.option3) {
      options.push({ name: "Style", value: variant.option3 });
    }

    return {
      id: `gid://shopify/ProductVariant/${variant.id}`,
      title: variant.title,
      price: {
        amount: variant.price || "0",
        currencyCode: "GBP"
      },
      availableForSale: variant.available !== false,
      selectedOptions: options.length > 0 ? options : [{ name: "Size", value: "Default" }]
    };
  });

  // Determine product type based on title/tags
  const titleLower = (product.title || "").toLowerCase();
  let productType = product.product_type || "";
  
  if (!productType) {
    if (titleLower.includes("earring") || titleLower.includes("hoop") || 
        titleLower.includes("stud") || titleLower.includes("huggie")) {
      productType = "Earrings";
    } else if (titleLower.includes("bracelet") || titleLower.includes("chain") || 
               titleLower.includes("bangle") || titleLower.includes("cuff")) {
      productType = "Bracelets";
    } else {
      productType = "Jewelry";
    }
  }

  const prices = (product.variants || []).map(v => parseFloat(v.price || "0"));
  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

  return {
    id: `gid://shopify/Product/${product.id}`,
    title: product.title,
    handle: product.handle,
    description: product.body_html ? stripHtml(product.body_html).substring(0, 500) : "",
    descriptionHtml: product.body_html || "",
    productType,
    tags: product.tags ? (typeof product.tags === 'string' ? product.tags.split(', ') : product.tags) : [],
    price: String(minPrice),
    currency: "GBP",
    maxPrice: String(maxPrice),
    images,
    variants,
    availableForSale: (product.variants || []).some(v => v.available !== false)
  };
};

/**
 * Fetch all products from Shopify (with caching)
 */
const fetchAllProducts = async () => {
  const now = Date.now();
  
  // Return cached data if valid
  if (productsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return productsCache;
  }

  try {
    let response;
    
    if (isProduction) {
      // Use Netlify Function in production
      response = await axios.get('/.netlify/functions/products', { timeout: 30000 });
    } else {
      // In development, try the backend API first, then fall back to CORS proxy
      try {
        response = await axios.get('/api/products', { timeout: 10000 });
        // Backend returns data in different format
        if (response.data.products) {
          productsCache = response.data.products;
          cacheTimestamp = now;
          console.log(`Fetched ${productsCache.length} products from backend`);
          return productsCache;
        }
      } catch (backendError) {
        console.log('Backend not available, using CORS proxy...');
        // Use a public CORS proxy for development
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://${SHOPIFY_STORE}/products.json?limit=250`)}`;
        response = await axios.get(proxyUrl, { timeout: 30000 });
      }
    }
    
    const products = response.data.products || [];
    productsCache = products.map(transformShopifyProduct);
    cacheTimestamp = now;
    
    console.log(`Fetched ${productsCache.length} products from Shopify`);
    return productsCache;
  } catch (error) {
    console.error('Error fetching from Shopify:', error);
    // Return cached data even if expired, or empty array
    return productsCache || [];
  }
};

// Products API
export const getProducts = async (collection = null) => {
  try {
    let products = await fetchAllProducts();
    
    // Filter by collection if specified
    if (collection) {
      const collectionLower = collection.toLowerCase();
      if (collectionLower.includes('bracelet')) {
        products = products.filter(p => p.productType === 'Bracelets');
      } else if (collectionLower.includes('earring')) {
        products = products.filter(p => p.productType === 'Earrings');
      }
    }
    
    return { collection, products };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const getProduct = async (handle) => {
  try {
    const products = await fetchAllProducts();
    const product = products.find(p => p.handle === handle);
    
    if (!product) {
      throw new Error('Product not found');
    }
    
    return product;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

export const getCollections = async () => {
  try {
    const products = await fetchAllProducts();
    
    // Count products by type
    const braceletCount = products.filter(p => p.productType === 'Bracelets').length;
    const earringCount = products.filter(p => p.productType === 'Earrings').length;
    
    // Get sample images
    const braceletImage = products.find(p => p.productType === 'Bracelets')?.images?.[0]?.url;
    const earringImage = products.find(p => p.productType === 'Earrings')?.images?.[0]?.url;
    
    return [
      {
        handle: 'bracelets',
        title: 'Bracelets',
        description: `Our signature collection of ${braceletCount} waterproof bracelets, featuring 18K gold plated and stainless steel designs.`,
        image: { 
          url: braceletImage || 'https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=800'
        }
      },
      {
        handle: 'earrings',
        title: 'Earrings',
        description: `${earringCount} elegant earrings designed to complement any outfit, from subtle studs to statement drops.`,
        image: { 
          url: earringImage || 'https://images.unsplash.com/photo-1584948221378-93841f589fb7?w=800'
        }
      }
    ];
  } catch (error) {
    console.error('Error fetching collections:', error);
    throw error;
  }
};

// Cart functionality - uses localStorage and redirects to Shopify checkout
export const createCart = async (items) => {
  const cartId = `cart_${Date.now()}`;
  const cart = { id: cartId, items: items || [] };
  localStorage.setItem('luxurystyle_cart', JSON.stringify(cart));
  return cart;
};

export const addToCart = async (cartId, items) => {
  const stored = localStorage.getItem('luxurystyle_cart');
  const cart = stored ? JSON.parse(stored) : { id: cartId, items: [] };
  cart.items = [...cart.items, ...items];
  localStorage.setItem('luxurystyle_cart', JSON.stringify(cart));
  return cart;
};

export const getCart = async (cartId) => {
  const stored = localStorage.getItem('luxurystyle_cart');
  return stored ? JSON.parse(stored) : { id: cartId, items: [] };
};

// Contact form - uses Netlify Forms (form submission handled by Netlify)
export const submitContact = async (formData) => {
  // Netlify Forms handles this automatically via the form in index.html
  // This is just a backup/placeholder
  console.log('Contact form submitted:', formData);
  return { success: true, message: 'Form submitted successfully' };
};

// Format price
export const formatPrice = (amount, currency = 'GBP') => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
  }).format(parseFloat(amount));
};

// Get variant ID from Shopify global ID
export const extractVariantId = (globalId) => {
  // Format: gid://shopify/ProductVariant/123456789
  return globalId.split('/').pop();
};
